'use client'

import { PaperElevations } from '@snowball-tech/fractal/dist/src/components/Paper'

import { type ComponentProps, useState } from 'react'

import isNumber from 'lodash/fp/isNumber'
import omit from 'lodash/fp/omit'

import type { Progress } from '../../../api/endpoints'
import type { ChildrenProps, ImageSize } from '../../../types'

import { AllowedMediaSources, ObjectFits } from '../../../enums'
import { type HandleFileCallback, constraintSize } from '../../../helpers/files'
import FilePicker from '../FilePicker/FilePicker'
import LoremPicsum from './LoremPicsum'
import Trigger from './Trigger'

type Props = {
  allowedSources?: Array<AllowedMediaSources>
  deletable?: boolean
  editable?: boolean
  elevation?: '0' | PaperElevations
  handleFile?: HandleFileCallback
  objectFit?: ObjectFits
  placeholderText?: string
  resizable?: boolean
  side?: ComponentProps<typeof FilePicker>['side']
  size?: ImageSize
  small?: boolean
  url?: string
  withCaption?: boolean
  withPicker?: boolean
  onChange?: (
    url?: string,
    objectFit?: ObjectFits,
    padding?: 'none' | 'small',
    size?: ImageSize,
  ) => void
} & Omit<
  ComponentProps<typeof Trigger>,
  'onChange' | 'progress' | 'setIsImageLoading'
> &
  Partial<ChildrenProps>
export default function ImagePicker({
  allowedSources = [
    AllowedMediaSources.Paste,
    AllowedMediaSources.Upload,
    AllowedMediaSources.Link,
    AllowedMediaSources.LoremPicsum,
  ],
  children: caption,
  deletable = true,
  editable = true,
  elevation = PaperElevations.Elevated,
  handleFile,
  objectFit = ObjectFits.Contain,
  onChange,
  padding = 'none',
  placeholderText,
  resizable = true,
  side,
  size,
  small = false,
  url,
  withCaption = false,
  withPicker = true,
  ...triggerProps
}: Props) {
  const [isImageLoading, setIsImageLoading] = useState(false)
  const [progress, setProgress] = useState<Progress | null>(null)

  const handleImageUpdate = async (
    newUrl: string,
    _provider: string,
    newImageSize?: { duration: number } | { thumbnailUrl: string } | ImageSize,
  ) => {
    const newSize = newImageSize as ImageSize

    return onChange?.(
      newUrl,
      objectFit,
      padding,
      constraintSize(
        newSize?.width,
        isNumber(newSize?.height) ? newSize.height + 1 : newSize?.height,
        triggerProps.maxWidth,
      ),
    )
  }

  const handleObjectFitUpdate = async (newObjectFit: ObjectFits) =>
    onChange?.(url, newObjectFit, padding, size)

  const handlePaddingChange = async (newPadding: 'none' | 'small') =>
    onChange?.(url, objectFit, newPadding, size)

  const handleSizeChange = async (newSize: ImageSize) =>
    onChange?.(
      url,
      objectFit,
      padding,
      constraintSize(newSize?.width, newSize?.height, triggerProps.maxWidth, {
        constrainHeightToViewport: false,
      }),
    )

  return (
    <div className="flex flex-col gap-1">
      <FilePicker
        allowedSources={allowedSources}
        deletable={deletable}
        editable={editable}
        handleFile={handleFile}
        setIsLoading={setIsImageLoading}
        setProgress={setProgress}
        side={side}
        trigger={
          <Trigger
            buttonLabel={placeholderText}
            className={triggerProps.className}
            editable={editable}
            elevation={elevation}
            imageUrl={url}
            isImageLoading={isImageLoading}
            objectFit={objectFit}
            padding={padding}
            progress={progress}
            resizable={editable && resizable}
            setIsImageLoading={setIsImageLoading}
            size={size}
            small={small}
            onObjectFitChange={handleObjectFitUpdate}
            onPaddingChange={handlePaddingChange}
            onSizeChange={handleSizeChange}
            {...omit(
              ['className', 'setIsImageLoading', 'progress'],
              triggerProps,
            )}
          />
        }
        type="image"
        withPicker={withPicker}
        onChange={handleImageUpdate}
      >
        {allowedSources.includes(AllowedMediaSources.LoremPicsum) && (
          <LoremPicsum
            setIsLoading={setIsImageLoading}
            onPick={handleImageUpdate}
          />
        )}
      </FilePicker>

      {withCaption && caption}
    </div>
  )
}
